export class SurveyFlow {
  constructor(questions, options) {
    this.questions = questions
    this.container = options.container
    this.onComplete = options.onComplete
    this.honeypotName = options.honeypotName || "_hp_email"
    this.currentIndex = 0
    this.answers = {}
  }

  start() {
    this.container.innerHTML = ""

    const wrapper = document.createElement("div")
    wrapper.className = "survey-widget__flow"
    this.wrapper = wrapper

    const progress = document.createElement("div")
    progress.className = "survey-widget__progress"
    this.progressEl = progress
    wrapper.appendChild(progress)

    const slideArea = document.createElement("div")
    slideArea.className = "survey-widget__slide-area"
    this.slideArea = slideArea
    wrapper.appendChild(slideArea)

    // Honeypot
    const hp = document.createElement("input")
    hp.type = "text"
    hp.name = this.honeypotName
    hp.tabIndex = -1
    hp.autocomplete = "off"
    hp.style.cssText = "position:absolute;left:-9999px;opacity:0;height:0;width:0;"
    hp.setAttribute("aria-hidden", "true")
    wrapper.appendChild(hp)

    this.container.appendChild(wrapper)
    this.showQuestion(0)
  }

  showQuestion(index, direction = "forward") {
    this.currentIndex = index
    this.updateProgress()

    const question = this.questions[index]
    const slide = this.buildQuestionSlide(question)

    if (this.currentSlide) {
      const outClass = direction === "forward" ? "survey-widget__slide--exit-up" : "survey-widget__slide--exit-down"
      this.currentSlide.classList.add(outClass)

      setTimeout(() => {
        this.currentSlide?.remove()
        this.slideArea.appendChild(slide)
        requestAnimationFrame(() => slide.classList.add("survey-widget__slide--active"))
        this.currentSlide = slide
      }, 250)
    } else {
      this.slideArea.appendChild(slide)
      requestAnimationFrame(() => slide.classList.add("survey-widget__slide--active"))
      this.currentSlide = slide
    }
  }

  buildQuestionSlide(question) {
    const slide = document.createElement("div")
    slide.className = "survey-widget__slide"

    let html = `<p class="survey-widget__q-prompt">${this.esc(question.prompt)}</p>`
    if (question.description) {
      html += `<p class="survey-widget__q-desc">${this.esc(question.description)}</p>`
    }

    html += this.buildInputHtml(question)
    html += this.buildNavHtml()

    slide.innerHTML = html
    this.bindInputEvents(slide, question)
    this.bindNavEvents(slide)

    return slide
  }

  buildInputHtml(question) {
    const qId = question.id
    const savedVal = this.answers[qId]

    switch (question.type) {
      case "multiple_choice":
      case "yes_no":
        return this.buildChoiceHtml(question, savedVal)
      case "image_choice":
        return this.buildImageChoiceHtml(question, savedVal)
      case "free_text":
        return `<textarea class="survey-widget__textarea" data-qid="${qId}"
                  maxlength="${question.max_length || 500}"
                  placeholder="Type your answer...">${this.esc(savedVal || "")}</textarea>`
      case "range_slider":
        return this.buildSliderHtml(question, savedVal)
      case "rating":
        return this.buildRatingHtml(question, savedVal)
      default:
        return `<input type="text" class="survey-widget__input" data-qid="${qId}" value="${this.esc(savedVal || "")}">`
    }
  }

  buildChoiceHtml(question, savedVal) {
    const options = question.options || [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No" }
    ]
    return `<div class="survey-widget__choices" data-qid="${question.id}">
      ${options.map(opt => `
        <button class="survey-widget__choice ${savedVal === opt.value ? "survey-widget__choice--selected" : ""}"
                data-value="${this.esc(opt.value)}">
          ${this.esc(opt.label)}
        </button>
      `).join("")}
    </div>`
  }

  buildImageChoiceHtml(question, savedVal) {
    return `<div class="survey-widget__image-choices" data-qid="${question.id}">
      ${(question.options || []).map(opt => `
        <button class="survey-widget__image-choice ${savedVal === opt.value ? "survey-widget__image-choice--selected" : ""}"
                data-value="${this.esc(opt.value)}">
          ${opt.image_url ? `<img src="${this.esc(opt.image_url)}" alt="${this.esc(opt.label)}" class="survey-widget__choice-img">` : ""}
          <span class="survey-widget__choice-label">${this.esc(opt.label)}</span>
        </button>
      `).join("")}
    </div>`
  }

  buildSliderHtml(question, savedVal) {
    const min = question.min ?? 0
    const max = question.max ?? 100
    const step = question.step ?? 1
    const val = savedVal ?? Math.round((min + max) / 2)
    return `<div class="survey-widget__slider-wrap" data-qid="${question.id}">
      <input type="range" class="survey-widget__slider" min="${min}" max="${max}" step="${step}" value="${val}">
      <div class="survey-widget__slider-labels">
        <span>${this.esc(question.label_min || String(min))}</span>
        <span class="survey-widget__slider-value">${val}</span>
        <span>${this.esc(question.label_max || String(max))}</span>
      </div>
    </div>`
  }

  buildRatingHtml(question, savedVal) {
    const max = question.rating_max || 5
    const icon = question.rating_icon || "star"
    const icons = { star: "&#9733;", heart: "&#9829;", thumbs: "&#128077;" }
    const sym = icons[icon] || icons.star

    let html = `<div class="survey-widget__rating" data-qid="${question.id}">`
    for (let i = 1; i <= max; i++) {
      const activeClass = savedVal && parseInt(savedVal) >= i ? "survey-widget__rating-icon--active" : ""
      html += `<button class="survey-widget__rating-icon ${activeClass}" data-value="${i}">${sym}</button>`
    }
    html += "</div>"
    return html
  }

  buildNavHtml() {
    const isFirst = this.currentIndex === 0
    const isLast = this.currentIndex === this.questions.length - 1
    return `<div class="survey-widget__nav">
      ${!isFirst ? '<button class="survey-widget__btn survey-widget__btn--secondary survey-widget__nav-prev">Back</button>' : "<span></span>"}
      <button class="survey-widget__btn survey-widget__btn--primary survey-widget__nav-next">
        ${isLast ? "Submit" : "Next"}
      </button>
    </div>`
  }

  bindInputEvents(slide, question) {
    // Choice buttons
    slide.querySelectorAll(".survey-widget__choice, .survey-widget__image-choice").forEach(btn => {
      btn.addEventListener("click", () => {
        slide.querySelectorAll(".survey-widget__choice, .survey-widget__image-choice").forEach(b =>
          b.classList.remove("survey-widget__choice--selected", "survey-widget__image-choice--selected"))
        btn.classList.add(
          btn.classList.contains("survey-widget__image-choice")
            ? "survey-widget__image-choice--selected"
            : "survey-widget__choice--selected"
        )
        this.answers[question.id] = btn.dataset.value
      })
    })

    // Textarea
    const textarea = slide.querySelector(".survey-widget__textarea")
    if (textarea) {
      textarea.addEventListener("input", () => {
        this.answers[question.id] = textarea.value
      })
    }

    // Slider
    const slider = slide.querySelector(".survey-widget__slider")
    if (slider) {
      const display = slide.querySelector(".survey-widget__slider-value")
      slider.addEventListener("input", () => {
        this.answers[question.id] = slider.value
        if (display) display.textContent = slider.value
      })
      if (!this.answers[question.id]) this.answers[question.id] = slider.value
    }

    // Rating
    slide.querySelectorAll(".survey-widget__rating-icon").forEach(btn => {
      btn.addEventListener("click", () => {
        const val = parseInt(btn.dataset.value)
        this.answers[question.id] = String(val)
        slide.querySelectorAll(".survey-widget__rating-icon").forEach((b, idx) => {
          b.classList.toggle("survey-widget__rating-icon--active", idx < val)
        })
      })
    })
  }

  bindNavEvents(slide) {
    const nextBtn = slide.querySelector(".survey-widget__nav-next")
    const prevBtn = slide.querySelector(".survey-widget__nav-prev")

    if (nextBtn) {
      nextBtn.addEventListener("click", () => this.handleNext())
    }
    if (prevBtn) {
      prevBtn.addEventListener("click", () => this.handlePrev())
    }
  }

  handleNext() {
    const question = this.questions[this.currentIndex]
    if (question.required && !this.answers[question.id]) {
      this.showValidationError("Please answer this question to continue.")
      return
    }

    if (this.currentIndex < this.questions.length - 1) {
      this.showQuestion(this.currentIndex + 1, "forward")
    } else {
      this.complete()
    }
  }

  handlePrev() {
    if (this.currentIndex > 0) {
      this.showQuestion(this.currentIndex - 1, "backward")
    }
  }

  complete() {
    const answersArray = Object.entries(this.answers).map(([questionId, value]) => ({
      question_id: questionId,
      value: value
    }))
    this.onComplete(answersArray)
  }

  showSubmitting() {
    this.container.innerHTML = `
      <div class="survey-widget__submitting">
        <div class="survey-widget__spinner"></div>
        <p class="survey-widget__desc">Submitting your response...</p>
      </div>`
  }

  showError(msg) {
    const existing = this.wrapper?.querySelector(".survey-widget__validation-error")
    if (existing) existing.remove()

    const errorEl = document.createElement("div")
    errorEl.className = "survey-widget__validation-error"
    errorEl.textContent = msg
    this.slideArea?.appendChild(errorEl)

    setTimeout(() => errorEl.remove(), 3000)
  }

  showValidationError(msg) {
    this.showError(msg)
  }

  updateProgress() {
    if (!this.progressEl) return
    const total = this.questions.length
    const current = this.currentIndex + 1
    const pct = (current / total) * 100

    this.progressEl.innerHTML = `
      <div class="survey-widget__progress-bar">
        <div class="survey-widget__progress-fill" style="width:${pct}%"></div>
      </div>
      <span class="survey-widget__progress-text">${current} of ${total}</span>`
  }

  esc(str) {
    if (!str) return ""
    const el = document.createElement("span")
    el.textContent = str
    return el.innerHTML
  }
}
